import { Component, OnInit, OnDestroy } from '@angular/core';
import { Restaurant } from '../models/restaurant.model';
import { ShopService } from '../services/shop.service';
import { OpenHour } from '../models/open-hour.model';
import { NavigationService } from '../services/navigation.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Order } from '../models/order.model';
import { ShopQueryParams } from '../models/shop-query-params.model';
import { TranslateService } from '@ngx-translate/core';
import { Category } from '../models/category.model';
import { Meta, Title } from '@angular/platform-browser';

@Component({
  selector: 'app-shop',
  templateUrl: './shop.component.html',
  styleUrls: ['./shop.component.scss']
})
export class ShopComponent implements OnInit, OnDestroy {

  restaurants$: any;
  restaurants: Array<Restaurant> = [];
  restaurant$: any;
  restaurant: Restaurant = null;
  openHours$: any;
  openHours: Array<OpenHour> = [];
  route$: any;
  shopQueryParams: ShopQueryParams = {
    restaurantId: null,
    orderType: null,
    col1: null,
    col2: null,
    lang: null,
    categoryId: null
  };
  shopQueryParams$: any;
  category: Category = null;
  category$: any;
  col1$: any;

  constructor(
    private shopService: ShopService,
    private nav: NavigationService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private meta: Meta,
    private title: Title
  ) {
    
   }

  ngOnInit() {
    this.title.setTitle(this.translate.instant('TITLE_TAG_SHOP'));
    this.meta.addTag({ name: 'description', content: this.translate.instant('META_DESCRIPTION_SHOP') });
    this.shopQueryParams$ = this.nav.shopQueryParams.subscribe((s) => {
      this.shopQueryParams = s;
      const sq = JSON.parse(JSON.stringify(s));
      if (s) {
        if (!this.shopService.order.value.orderItems) {
          sq.col2 = null;
        }
        this.router.navigate(['.'], { relativeTo: this.route, queryParams: sq });
      }

    });
    this.route$ = this.route.queryParams.subscribe(
      (params) => {
        if (params.restaurantId) {
          this.shopService.initRestaurant(parseInt(params.restaurantId));
        } else {
          this.shopService.restaurant.next(null);
        }
        if (params.lang) {
          if (params.lang === 'de' || params.lang === 'en') {
            this.translate.use(params.lang);
          }
        }
        if (params.orderType) {
          this.shopService.orderType.next(params.orderType);
        } else {
          this.shopService.orderType.next(null);
        }
        if (params.col1) {
          this.nav.col1.next(params.col1);
        } else {
          this.nav.col1.next(null);
        }
        if (params.col2) {
          if (this.shopService.order.value.orderItems) {
            this.nav.col2.next(params.col2);
          } else {
            this.nav.col2.next(null);
          }
        } else {
          this.nav.col2.next(null);
        }
        this.nav.shopQueryParams.next(params);
      }
    );
    this.category$ = this.shopService.category.subscribe(
      (r) => {
        this.category = r;
        if (this.category) {
          const sq = JSON.parse(JSON.stringify(this.nav.shopQueryParams.value));
          sq.categoryId = this.category.categoryId;
          this.nav.shopQueryParams.next(sq);
          this.title.setTitle(
            this.translate.instant('TITLE_TAG_RESTAURANT_CATEGORY', {
              restaurantName: this.shopService.restaurant.value.name,
              categoryName: this.getCategoryName(this.category)
            })
          );
          this.meta.updateTag({
            name: 'description',
            content: this.translate.instant('META_DESCRIPTION_RESTAURANT_CATEGORY', {
              restaurantName: this.shopService.restaurant.value.name,
              categoryName: this.getCategoryName(this.category)
            })
          });
          this.router.navigate(['.'], { relativeTo: this.route, queryParams: sq });
        }
      }
    );
    this.shopService.getRestaurants().subscribe(arg => {});
    this.shopService.getOpenHours().subscribe(arg => {});
    this.restaurants$ = this.shopService.restaurants.subscribe(
      (r) => {
        this.restaurants = r;
        if (this.shopQueryParams.restaurantId) {
          this.shopService.initRestaurant(parseInt(this.shopQueryParams.restaurantId));
        }
      }
    );
    this.restaurant$ = this.shopService.restaurant.subscribe(
      (r) => {
        this.restaurant = r;
        if (r && !this.category) {
          this.title.setTitle(
            this.translate.instant('TITLE_TAG_RESTAURANT', {
              restaurantName: r.name
            })
          );
          this.meta.updateTag({
            name: 'description',
            content: this.translate.instant('META_DESCRIPTION_RESTAURANT', {
              restaurantName: r.name
            })
          });
        }
        this.handleSEO();
      }
    );
    this.openHours$ = this.shopService.openHours.subscribe(
      (r) => {
        this.openHours = r;
      }
    );
    this.col1$ = this.nav.col1.subscribe((col1) => {
      this.handleSEO();
    });
  }

  ngOnDestroy() {
    this.restaurant$.unsubscribe();
    this.restaurants$.unsubscribe();
    this.openHours$.unsubscribe();
    this.route$.unsubscribe();
    this.shopQueryParams$.unsubscribe();
    this.category$.unsubscribe();
  }

  handleSEO() {
    const col1 = this.nav.col1.value;
    if (col1 === 'address' && this.restaurant) {
      // Delivery Tags
      this.title.setTitle(
        this.translate.instant('TITLE_TAG_RESTAURANT_DELIVERY', {
          restaurantName: this.restaurant.name
        })
      );
      this.meta.updateTag({
        name: 'description',
        content: this.translate.instant('META_DESCRIPTION_RESTAURANT_DELIVERY', {
          restaurantName: this.restaurant.name
        })
      });
    }
    if (col1 === 'categories' && this.restaurant && !this.category) {
      // Categories Tags
      this.title.setTitle(
        this.translate.instant(
          (this.shopService.orderType.value === 'takeout' ? 'TITLE_TAG_RESTAURANT_TAKEOUT_CATEGORIES' : 'TITLE_TAG_RESTAURANT_DELIVERY_CATEGORIES'),
          {
            restaurantName: this.restaurant.name,
          }
        )
      );
      this.meta.updateTag({
        name: 'description',
        content: this.translate.instant(
          (this.shopService.orderType.value === 'takeout' ? 'META_DESCRIPTION_RESTAURANT_TAKEOUT_CATEGORIES' : 'META_DESCRIPTION_RESTAURANT_DELIVERY_CATEGORIES'), {
            restaurantName: this.restaurant.name
          })
      });
    }
    if (col1 === 'menu' && this.restaurant) {
      // MENU Tags
      this.title.setTitle(
        this.translate.instant(
          'TITLE_TAG_RESTAURANT_MENU',
          {
            restaurantName: this.restaurant.name,
          }
        )
      );
      this.meta.updateTag({
        name: 'description',
        content: this.translate.instant(
          'META_DESCRIPTION_RESTAURANT_MENU', {
            restaurantName: this.restaurant.name
          })
      });
    }
    if (col1 === 'table-reservation' && this.restaurant) {
      // TABLE_RESERVATION Tags
      this.title.setTitle(
        this.translate.instant(
          'TITLE_TAG_RESTAURANT_TABLE_RESERVATION',
          {
            restaurantName: this.restaurant.name,
          }
        )
      );
      this.meta.updateTag({
        name: 'description',
        content: this.translate.instant(
          'META_DESCRIPTION_RESTAURANT_TABLE_RESERVATION', {
            restaurantName: this.restaurant.name
          })
      });
    }
    if (col1 === 'gallery' && this.restaurant) {
      // Categories Tags
      this.title.setTitle(
        this.translate.instant(
          'TITLE_TAG_RESTAURANT_GALLERY',
          {
            restaurantName: this.restaurant.name,
          }
        )
      );
      this.meta.updateTag({
        name: 'description',
        content: this.translate.instant(
          'META_DESCRIPTION_RESTAURANT_GALLERY', {
            restaurantName: this.restaurant.name
          })
      });
    }
  }

  selectRestaurant(restaurant: Restaurant) {
    this.nav.col1.next(0);
    this.nav.col2.next(0);
    const sq = JSON.parse(JSON.stringify(this.shopQueryParams));
    sq.restaurantId = restaurant === this.restaurant ? null : restaurant.restaurantId;
    sq.orderType = null;
    sq.col1 = null;
    sq.col2 = null;
    sq.lang = this.translate.currentLang;
    sq.categoryId = null;
    this.nav.shopQueryParams.next(sq);
    this.shopService.order.next(new Order());
    this.shopService.openDateTimes.next(null);
    this.shopService.orderType.next(null);
    this.shopService.selectedDateTime.next(null);
    this.shopService.category.next(null);
    this.router.navigate(['.'], { relativeTo: this.route, queryParams: sq });
  }

  restaurantOpenHours(type) {
    const oh = this.openHours.find(o => o.restaurantId === this.restaurant.restaurantId);
    return oh ? oh[type][0] : null;
  }

  formatTime(time) {
    return time.substr(0, time.length - 3);
  }

  getCategoryName(category: Category) {
    return category.categoryLanguages.find(o => o.languageKuerzel === this.translate.currentLang).name;
  }


}
